// src/component/AdminUserAttributes.js
// JSXあり / JS（TSなし）
// 依存: react, @mui/material, firebase v9
//
// 目的:
// - C案：users 全件（約5万件）をページングで読み込み、payments[] の paymentPhase でフィルタして都道府県別集計
// - 集計結果を1ドキュメントに保存し、以後はその1docだけ読む（Functions無し）
// - createdAt がある前提で「新規増分更新（createdAt差分）」ボタンを追加
//
// Firestore想定:
// - users/{uid}.payments: Array<{ paymentPhase: "1"|"2"|..., ... }>
// - users/{uid}.createdAt: Timestamp（存在）
// - users/{uid}.prefecture (または候補フィールド)
// - adminAggregates/userAttributesByPhase に集計結果を保存
//
// 注意:
// - updatedAt が無いので「既存ユーザの変更」を差分で完全追跡はしない
//   -> 日常は createdAt 差分（新規ユーザ分）だけ増分更新
//   -> 必要時に全件リビルド（全件スキャン）で補正

import React, { useContext, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Divider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  LinearProgress,
} from "@mui/material";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";

import {
  collection,
  getDocs,
  limit,
  query,
  startAfter,
  orderBy,
  where,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const PHASE_OPTIONS = ["1", "2", "3", "4"];

// 都道府県フィールド候補（必要に応じて増やす）
const PREF_FIELDS = ["prefecture", "residencePrefecture", "prefectureName", "pref"];

// 全件読み取りのページサイズ（Firestoreの体感的に 500〜2000 が無難）
const PAGE_SIZE = 1000;

// 簡易バー最大幅
const BAR_MAX_W = 360;

// 保存先（1doc）
const AGG_DOC_REF = doc(db, "adminAggregates", "userAttributesByPhase");

function normalizeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function pickPrefecture(user = {}) {
  for (const k of PREF_FIELDS) {
    const v = user?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "未設定";
}

function hasPhaseInPayments(user = {}, phases = []) {
  const pays = Array.isArray(user?.payments) ? user.payments : [];
  if (!pays.length) return false;

  // payments[] のどれかが指定phaseに一致したらヒット
  for (const p of pays) {
    const ph = normalizeStr(p?.paymentPhase);
    if (ph && phases.includes(ph)) return true;
  }
  return false;
}

function phaseKeyFromSelected(phases = []) {
  // ["2","1"] でも "1_2" になるように正規化
  const uniq = Array.from(new Set(phases.map(String))).filter(Boolean);
  uniq.sort((a, b) => Number(a) - Number(b));
  return uniq.join("_") || "none";
}

export default function AdminUserAttributes() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const surface = theme.surfaceBg ?? "#FFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  const [selectedPhases, setSelectedPhases] = useState(["4"]);
  const [filterText, setFilterText] = useState("");

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(""); // "scan" | "save" | "load" | "inc"
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const [totalUsers, setTotalUsers] = useState(0);
  const [prefCounts, setPrefCounts] = useState({});
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState("");

  // 進捗（全件読み取りの視認用：docsFetched は読み取った users 件数）
  const [docsFetched, setDocsFetched] = useState(0);

  // 中断フラグ（React stateだとループ中に遅延するので ref）
  const abortRef = useRef(false);

  const rows = useMemo(() => {
    const entries = Object.entries(prefCounts || {});
    const ft = filterText.trim();
    const filtered = ft ? entries.filter(([pref]) => pref.includes(ft)) : entries;

    filtered.sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0));
    return filtered.map(([pref, count]) => ({ pref, count: count ?? 0 }));
  }, [prefCounts, filterText]);

  const maxCount = useMemo(() => {
    let m = 0;
    for (const r of rows) m = Math.max(m, r.count || 0);
    return m;
  }, [rows]);

  const resetView = () => {
    setError("");
    setNote("");
    setTotalUsers(0);
    setPrefCounts({});
    setDocsFetched(0);
    setLastUpdatedLabel("");
  };

  const handleChangePhases = (_e, next) => {
    setSelectedPhases(Array.isArray(next) ? next : []);
  };

  const requirePhases = () => {
    if (!selectedPhases.length) {
      setError("paymentPhase を1つ以上選択してください。");
      return false;
    }
    return true;
  };

  const stop = () => {
    abortRef.current = true;
  };

  // --- C案：users全件スキャンして集計（ローカル集計） ---
  // returns: { total, prefCounts, phases, maxCreatedAt }
  const scanAllUsersAndAggregate = async () => {
    setError("");
    setNote("");
    setDocsFetched(0);
    setLastUpdatedLabel("");

    if (!requirePhases()) return null;

    const phases = selectedPhases.map(String);
    const nextCounts = {};
    let nextTotal = 0;

    abortRef.current = false;

    let lastDoc = null;
    let fetched = 0;

    // 全件スキャン中に「最大 createdAt」を追跡（増分更新の起点にする）
    let maxCreatedAt = null;

    while (true) {
      if (abortRef.current) {
        setNote("中断しました。");
        break;
      }

      const q = lastDoc
        ? query(collection(db, "users"), startAfter(lastDoc), limit(PAGE_SIZE))
        : query(collection(db, "users"), limit(PAGE_SIZE));

      const snap = await getDocs(q);
      if (snap.empty) break;

      for (const d of snap.docs) {
        const data = d.data() || {};

        // max createdAt 更新（存在しないユーザもいるかもしれないので吸収）
        const ca = data?.createdAt || null;
        if (ca && ca.toMillis) {
          if (!maxCreatedAt || ca.toMillis() > maxCreatedAt.toMillis()) {
            maxCreatedAt = ca;
          }
        }

        if (!hasPhaseInPayments(data, phases)) continue;

        const pref = pickPrefecture(data);
        nextCounts[pref] = (nextCounts[pref] || 0) + 1;
        nextTotal += 1;
      }

      fetched += snap.size;
      setDocsFetched(fetched);

      lastDoc = snap.docs[snap.docs.length - 1];
      if (snap.size < PAGE_SIZE) break;
    }

    return {
      total: nextTotal,
      prefCounts: nextCounts,
      phases,
      maxCreatedAt,
    };
  };

  // --- 全件集計（表示のみ） ---
  const doScan = async () => {
    if (loading) return;
    resetView();

    setLoading(true);
    setMode("scan");
    try {
      const result = await scanAllUsersAndAggregate();
      if (!result) return;

      setTotalUsers(result.total);
      setPrefCounts(result.prefCounts);
      if (!result.total && !abortRef.current) setNote("該当ユーザが見つかりませんでした。");
    } catch (e) {
      console.error(e);
      setError(e?.message || "全件集計中にエラーが発生しました。");
    } finally {
      setLoading(false);
      setMode("");
    }
  };

  // --- 全件リビルドして 1doc に保存 ---
  const doSaveAggregateDoc = async () => {
    if (loading) return;
    resetView();

    setLoading(true);
    setMode("save");
    try {
      const result = await scanAllUsersAndAggregate();
      if (!result) return;

      // 画面にも反映
      setTotalUsers(result.total);
      setPrefCounts(result.prefCounts);

      const key = phaseKeyFromSelected(result.phases);

      const payload = {
        total: result.total,
        prefCounts: result.prefCounts,
        phases: result.phases,
        updatedAt: serverTimestamp(),
        source: "manualBatch",
        pageSize: PAGE_SIZE,
        // 増分更新の起点（全件の最大 createdAt）
        lastRunCreatedAt: result.maxCreatedAt || serverTimestamp(),
      };

      await setDoc(
        AGG_DOC_REF,
        {
          [key]: payload,
          _meta: {
            updatedAt: serverTimestamp(),
            source: "manualBatch",
          },
        },
        { merge: true }
      );

      setLastUpdatedLabel(`保存しました（key: ${key}）`);
      if (!result.total && !abortRef.current) setNote("該当ユーザが見つかりませんでした（0件で保存）。");
    } catch (e) {
      console.error(e);
      setError(e?.message || "保存用集計中にエラーが発生しました。");
    } finally {
      setLoading(false);
      setMode("");
    }
  };

  // --- 保存済み集計を 1doc から読み込む ---
  const doLoadFromAggregateDoc = async () => {
    if (loading) return;
    resetView();
    if (!requirePhases()) return;

    setLoading(true);
    setMode("load");
    try {
      const key = phaseKeyFromSelected(selectedPhases);
      const snap = await getDoc(AGG_DOC_REF);

      if (!snap.exists()) {
        setNote("保存済み集計ドキュメントが存在しません。先に「集計ドキュメント更新（全件）」を実行してください。");
        return;
      }

      const data = snap.data() || {};
      const block = data?.[key];

      if (!block) {
        setNote(`この条件（key: ${key}）の保存データがありません。先に「集計ドキュメント更新（全件）」を実行してください。`);
        return;
      }

      setTotalUsers(Number(block.total || 0));
      setPrefCounts(block.prefCounts || {});

      setLastUpdatedLabel(
        block?.updatedAt?.toDate
          ? `保存データ読込（更新: ${block.updatedAt.toDate().toLocaleString()} / key: ${key}）`
          : `保存データ読込（key: ${key}）`
      );
    } catch (e) {
      console.error(e);
      setError(e?.message || "保存済みデータの読み込みに失敗しました。");
    } finally {
      setLoading(false);
      setMode("");
    }
  };

  // --- 新規増分更新（createdAt 差分） ---
  // 前提:
  // - すでに「全件→保存」を一度行い、該当 key ブロックが存在している
  // - users.createdAt が存在している（今回OK）
  //
  // 挙動:
  // - adminAggregates/userAttributesByPhase の key ブロックから lastRunCreatedAt を取得
  // - users where createdAt > lastRunCreatedAt をページングで読み
  // - payments[].paymentPhase が選択に含まれるユーザのみ、県カウントを加算
  // - ブロックの total / prefCounts / lastRunCreatedAt を更新して保存
  const doIncrementalByCreatedAt = async () => {
    if (loading) return;
    resetView();
    if (!requirePhases()) return;

    setLoading(true);
    setMode("inc");
    abortRef.current = false;

    try {
      const key = phaseKeyFromSelected(selectedPhases);
      const phases = selectedPhases.map(String);

      const aggSnap = await getDoc(AGG_DOC_REF);
      if (!aggSnap.exists()) {
        setNote("保存済み集計がありません。先に「集計ドキュメント更新（全件）」を実行してください。");
        return;
      }

      const agg = aggSnap.data() || {};
      const block = agg?.[key];

      if (!block) {
        setNote(`この条件（key: ${key}）の保存データがありません。先に「集計ドキュメント更新（全件）」を実行してください。`);
        return;
      }

      const lastRun = block?.lastRunCreatedAt;
      if (!lastRun || !lastRun.toMillis) {
        setNote(`lastRunCreatedAt がありません。先に「集計ドキュメント更新（全件）」を実行してください。（key: ${key}）`);
        return;
      }

      // 既存集計を読み込み（ローカルで加算して、最後に1回 setDoc）
      const nextCounts = { ...(block.prefCounts || {}) };
      let nextTotal = Number(block.total || 0);

      let fetched = 0;
      let lastDoc = null;

      // これが次回の差分起点になる（今回見た最大 createdAt）
      let maxCreatedAt = lastRun;

      while (true) {
        if (abortRef.current) {
          setNote("中断しました。");
          break;
        }

        // createdAt > lastRun の新規ユーザのみ
        // ※ orderBy + where のため、必要ならインデックス作成が求められる
        const q = lastDoc
          ? query(
              collection(db, "users"),
              orderBy("createdAt", "asc"),
              where("createdAt", ">", lastRun),
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(
              collection(db, "users"),
              orderBy("createdAt", "asc"),
              where("createdAt", ">", lastRun),
              limit(PAGE_SIZE)
            );

        const snap = await getDocs(q);
        if (snap.empty) break;

        for (const d of snap.docs) {
          const data = d.data() || {};

          const ca = data?.createdAt;
          if (ca && ca.toMillis && ca.toMillis() > maxCreatedAt.toMillis()) {
            maxCreatedAt = ca;
          }

          if (!hasPhaseInPayments(data, phases)) continue;

          const pref = pickPrefecture(data);
          nextCounts[pref] = (nextCounts[pref] || 0) + 1;
          nextTotal += 1;
        }

        fetched += snap.size;
        setDocsFetched(fetched);

        lastDoc = snap.docs[snap.docs.length - 1];
        if (snap.size < PAGE_SIZE) break;
      }

      // 1docに保存（keyブロックを更新）
      await setDoc(
        AGG_DOC_REF,
        {
          [key]: {
            ...block,
            total: nextTotal,
            prefCounts: nextCounts,
            phases,
            updatedAt: serverTimestamp(),
            source: "manualIncrement",
            lastRunCreatedAt: maxCreatedAt,
          },
          _meta: {
            updatedAt: serverTimestamp(),
            source: "manualIncrement",
          },
        },
        { merge: true }
      );

      // 画面反映
      setTotalUsers(nextTotal);
      setPrefCounts(nextCounts);

      setLastUpdatedLabel(
        `増分更新しました（新規読込 ${fetched.toLocaleString()} 件 / key: ${key}）`
      );
    } catch (e) {
      console.error(e);
      setError(e?.message || "増分更新に失敗しました。");
    } finally {
      setLoading(false);
      setMode("");
    }
  };

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
        >
          ADMIN
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}>
          ユーザ属性（都道府県別）
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          users 全件を読み取り、payments[].paymentPhase で抽出して都道府県別に集計します。結果は 1doc 保存し、以後は軽量に読み込めます。createdAt 差分で新規ユーザ分だけ増分更新できます。
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 2.5,
        }}
      >
        <CardContent sx={{ p: 2.2 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <Box sx={{ minWidth: 240 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: textBase, mb: 0.5 }}>
                  paymentPhase（複数選択）
                </Typography>

                <ToggleButtonGroup
                  value={selectedPhases}
                  onChange={handleChangePhases}
                  size="small"
                  color="primary"
                >
                  {PHASE_OPTIONS.map((p) => (
                    <ToggleButton key={p} value={p} sx={{ px: 2 }}>
                      {p}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: 1 }} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
                <Button
                  variant="contained"
                  startIcon={
                    loading && mode === "scan" ? <CircularProgress size={18} /> : <SearchRoundedIcon />
                  }
                  onClick={doScan}
                  disabled={loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  全件集計（直接）
                </Button>

                <Button
                  variant="outlined"
                  startIcon={
                    loading && mode === "save" ? <CircularProgress size={18} /> : <SaveRoundedIcon />
                  }
                  onClick={doSaveAggregateDoc}
                  disabled={loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  集計ドキュメント更新（全件→保存）
                </Button>

                <Button
                  variant="outlined"
                  startIcon={
                    loading && mode === "inc" ? <CircularProgress size={18} /> : <AddCircleOutlineRoundedIcon />
                  }
                  onClick={doIncrementalByCreatedAt}
                  disabled={loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  新規増分更新（createdAt差分）
                </Button>

                <Button
                  variant="outlined"
                  startIcon={
                    loading && mode === "load" ? <CircularProgress size={18} /> : <DownloadRoundedIcon />
                  }
                  onClick={doLoadFromAggregateDoc}
                  disabled={loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  保存済みを読み込む（1doc）
                </Button>

                <Button
                  variant="text"
                  onClick={stop}
                  disabled={!loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  中断
                </Button>

                <Button
                  variant="text"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={() => {
                    abortRef.current = false;
                    resetView();
                    setError("");
                    setNote("");
                  }}
                  disabled={loading}
                  sx={{ fontWeight: 900, borderRadius: 2 }}
                >
                  リセット
                </Button>
              </Stack>
            </Stack>

            <Divider />

            {loading ? (
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
                  処理中：users 読み取り {docsFetched.toLocaleString()} 件…
                </Typography>
                <LinearProgress />
              </Stack>
            ) : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, color: textBase }}>
                合計: {totalUsers.toLocaleString()} 人
              </Typography>

              <Box sx={{ flex: 1 }} />

              <TextField
                size="small"
                label="都道府県フィルタ（表示のみ）"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ width: { xs: "100%", sm: 280 } }}
              />
            </Stack>

            {lastUpdatedLabel ? <Alert severity="success">{lastUpdatedLabel}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            {note ? <Alert severity="info">{note}</Alert> : null}

            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
              注意：全件更新は users を全件読み取ります。運用では「全件→保存」を最初に1回、その後は「保存済み読込」＋必要時に「新規増分更新」を基本にしてください。既存ユーザの県変更/支払い追加は updatedAt が無いので、定期的な全件リビルドで補正してください。
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Stack spacing={2.5}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent sx={{ p: 2.2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: textBase, mb: 1 }}>
              都道府県別（簡易グラフ）
            </Typography>

            {rows.length === 0 ? (
              <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)" }}>
                まだデータがありません。上のボタンで集計してください。
              </Typography>
            ) : (
              <Stack spacing={1}>
                {rows.slice(0, 30).map((r) => {
                  const w = maxCount ? Math.round((r.count / maxCount) * BAR_MAX_W) : 0;
                  return (
                    <Stack key={r.pref} direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ width: 96, minWidth: 96 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: textBase }} noWrap>
                          {r.pref}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          height: 12,
                          width: w,
                          minWidth: 6,
                          borderRadius: 999,
                          background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                          boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                        }}
                      />

                      <Box sx={{ minWidth: 64 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: textBase }}>
                          {r.count.toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}

                {rows.length > 30 ? (
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)", mt: 1 }}>
                    上位30都道府県のみ表示中（表は全件）
                  </Typography>
                ) : null}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          }}
        >
          <CardContent sx={{ p: 2.2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: textBase, mb: 1 }}>
              都道府県別（一覧）
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>都道府県</TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    人数
                  </TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    構成比
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const pct = totalUsers ? (r.count / totalUsers) * 100 : 0;
                  return (
                    <TableRow key={r.pref}>
                      <TableCell>{r.pref}</TableCell>
                      <TableCell align="right">{r.count.toLocaleString()}</TableCell>
                      <TableCell align="right">{pct.toFixed(1)}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
